import React, { useEffect, useMemo, useState } from 'react'
import { Button, makeStyles, mergeClasses, tokens } from '@fluentui/react-components'
import { ChevronLeft24Regular, ChevronRight24Regular } from '@fluentui/react-icons'
import { Cell, Label, LabelList, Pie, PieChart } from 'recharts'
import {
   getActivitiesInPeriod,
   getActivityLabelClusters,
   getPeriodDateRange,
   months,
   quarters,
   stackOverlappingActivities,
   weeks,
} from '../../helpers/calendarHelper'
import dayjs from 'dayjs'
import isoWeeksInYear from 'dayjs/plugin/isoWeeksInYear'
import isLeapYear from 'dayjs/plugin/isLeapYear'
import isoWeek from 'dayjs/plugin/isoWeek'
import dayOfYear from 'dayjs/plugin/dayOfYear'
import { Activity } from '../../api/schemas/schema'
import { ActivityTooltip } from './ActivityTooltip'
import { ClusterCountBadge } from './ClusterCountBadge'
import { ModalActivityList } from './ModalActivityList'
import { t } from '../../i18n/i18n'
dayjs.extend(isoWeeksInYear)
dayjs.extend(dayOfYear)
dayjs.extend(isoWeeksInYear)
dayjs.extend(isoWeek)
dayjs.extend(isoWeeksInYear)
dayjs.extend(isLeapYear)

const CLUSTER_LEVEL_HEIGHT = 12 // px per stack level for dense clusters, vs. the helper's default 20px — kept wide enough to stay clickable
const CLUSTER_MAX_LEVEL = 3 // caps how far a dense cluster stacks outward; extra members pile at this radius instead of growing the chart forever
const MAX_ACTIVITY_OFFSET = CLUSTER_MAX_LEVEL * CLUSTER_LEVEL_HEIGHT // hard ceiling any activity can reach — used as a constant so the chart's own size never depends on activity count

/**
 * Hover affordance for every clickable sector: its fill fades to this opacity.
 *
 * A single relative value rather than a hover colour per ring, because the rings do not share a
 * colour — the quarter ring is '#FFDF9E', month and week use colorBrandBackground3Static — so one
 * explicit hover colour cannot serve all of them, while an explicit colour per ring would have to be
 * re-picked (and, for the quarter ring, hard-coded outside the Fluent palette) for every ring added.
 * Fading is colour-agnostic: it holds for any fill and follows along if a fill ever changes.
 */
const HOVER_FADE_OPACITY = 0.72

type PeriodRing = {
   periodType: 'quarter' | 'month' | 'week'
   /** label = the glyph drawn in the ring AND the value handed to onPeriodClick; null = spacer cell */
   data: { label: string | number | null; value: number }[]
   innerRadius: number
   outerRadius: number
   fill: string
   /**
    * Omit to inherit Pie's default stroke ('#fff') — the white separators between sectors.
    * Never pass `undefined` explicitly: recharts spreads Cell props over the Pie's presentation props
    * (Pie.getRealPieData) and JSX keeps explicit undefined keys, so `stroke={undefined}` would erase them.
    */
   cellStroke?: string
   labelClass: string
}

type CalendarChartProps = {
   activities: Activity[]
   year: {
      name: number
      value: number
   }
   setYear: (year: { name: number; value: number }) => void
   onActivitySelect: (activity: Activity) => void
}
const useStyles = makeStyles({
   container: {
      display: 'flex',
      justifyContent: 'center',
      overflow: 'scrollable',
      height: '100vh',
      '& *': {
         WebkitTapHighlightColor: 'transparent',
      },
      '& *:focus, & *:focus-visible': {
         outline: 'none',
      },
   },
   year: {
      fontSize: tokens.fontSizeHero800,
      fontWeight: tokens.fontWeightSemibold,
      fontFamily: tokens.fontFamilyBase,
      color: tokens.colorStrokeFocus2,
   },
   quarters: {
      fontSize: tokens.fontSizeBase600,
      fontFamily: tokens.fontFamilyBase,
      fill: tokens.colorNeutralForeground1,
      stroke: tokens.colorNeutralForeground1,
   },
   months: {
      fontSize: tokens.fontSizeBase500,
      fontFamily: tokens.fontFamilyBase,
      fill: tokens.colorNeutralForegroundOnBrand,
   },
   weeks: {
      fontSize: tokens.fontSizeBase400,
      fontFamily: tokens.fontFamilyBase,
      fill: tokens.colorNeutralForegroundOnBrand,
   },
   /**
    * The interactive styling of a period sector, in one class: the pointer cursor and the hover fade.
    *
    * Only the fill fades. `filter: brightness()` would have been the other relative option, but a
    * filter applies to the whole element, so it would also darken the sector's stroke — the light
    * separator grid between sectors (see PeriodRing['cellStroke']) would go grey on hover.
    * fill-opacity leaves stroke untouched. For the same reason strokeOpacity must NOT be copied here
    * from activityArc, where the stroke is the arc's own colour rather than a separator.
    *
    * The fade stays lit while the pointer crosses a label glyph: classes.periodLabel keeps the <text>
    * pointer-transparent, so the sector below remains the hover target and the effect cannot flicker
    * between the colour surface and the letters of "Q1".
    *
    * Applied to every non-empty cell (see the <Cell> below), which is what keeps the effect an
    * invariant of the ring config: a ring added to periodRings inherits the affordance instead of
    * having to declare a hover colour of its own. Spacer weeks are not clickable and never get it.
    *
    * Class-based `fill-opacity` beats the presentation attributes recharts writes on the <path>, so
    * this needs no !important.
    */
   periodCell: {
      cursor: 'pointer',
      transitionProperty: 'fill-opacity',
      transitionDuration: tokens.durationFaster,
      transitionTimingFunction: tokens.curveEasyEase,
      ':hover': {
         fillOpacity: HOVER_FADE_OPACITY,
      },
   },
   /**
    * The sector <path> (periodCell) is the ONLY interaction surface of a period ring.
    *
    * recharts renders <LabelList> text as a SIBLING of the sectors, painted on top of them
    * (Pie.render -> renderSectors(), then LabelList.renderCallByParent()). A label that accepts
    * pointer events therefore steals hover/click from the sector it is drawn over:
    *   - `cursor` is not inherited, because the <text> is not a descendant of the <path>
    *   - the <text> becomes the event target, so the sector's own handler is bypassed
    * Making the labels pointer-transparent collapses ring + label back into one hit target.
    *
    * Compose this into EVERY period-ring label class. A period label styled without it is a bug.
    */
   periodLabel: {
      pointerEvents: 'none',
   },
   /**
    * Activity labels deliberately keep their own cursor and handlers instead of using periodLabel:
    * they are drawn OUTSIDE their arc (outerRadius + 20) via Pie.renderLabels, which calls
    * filterProps(props, false) — recharts forwards no events to them, and there is no sector
    * underneath to delegate to. Do not "unify" this with periodLabel.
    */
   activityLabel: {
      fontSize: tokens.fontSizeBase400,
      fill: tokens.colorBrandForeground2Pressed,
      cursor: 'pointer',
      stroke: 'none',
   },
   /**
    * Activity arcs are clickable as well, so they share the period rings' relative hover fade — and
    * have to, since an arc's fill is the activity's own colour, which no static hover colour could
    * anticipate either.
    *
    * Unlike a period cell, an arc is stroked in its OWN fill colour (see stroke={entry.color} on the
    * Pie), so its stroke-opacity has to fade with the fill; fading only the fill would frame a
    * washed-out body in a fully opaque outline of the original colour.
    *
    * Lives on the <Cell>, not in the Pie's inline style, so the sector <path> — the actual hit target,
    * and the element :hover resolves against — is the single place an arc's interactive styling sits.
    * The arc's tooltip is driven by onMouseMove/onMouseLeave on the same Cell and is unaffected: this
    * is pure CSS and triggers no re-render.
    */
   activityArc: {
      cursor: 'pointer',
      transitionProperty: 'fill-opacity, stroke-opacity',
      transitionDuration: tokens.durationFaster,
      transitionTimingFunction: tokens.curveEasyEase,
      ':hover': {
         fillOpacity: HOVER_FADE_OPACITY,
         strokeOpacity: HOVER_FADE_OPACITY,
      },
   },
})

export const CalendarChart = ({ activities, year, setYear, onActivitySelect }: CalendarChartProps) => {
   const classes = useStyles()
   const [screenWidth, setScreenWidth] = useState(window.innerWidth)

   useEffect(() => {
      const handleResize = () => setScreenWidth(window.innerWidth)
      window.addEventListener('resize', handleResize)
      return () => window.removeEventListener('resize', handleResize)
   }, [])

   const stackedActivities = useMemo(() => stackOverlappingActivities(activities, year.name), [activities, year.name])
   const activityClusters = useMemo(() => getActivityLabelClusters(activities, year.name), [activities, year.name])

   // Clusters of 3+ activities collapse into a single "+N" badge instead of overlapping text labels.
   // The representative (the one that carries the badge) is the member stacked highest, so the badge
   // naturally sits above the whole stack rather than in the middle of it.
   const clusterMeta = useMemo(() => {
      const meta = new Map<string, { size: number; representativeId: string; memberIds: string[] }>()
      activityClusters.forEach((memberIds) => {
         if (memberIds.length <= 2) return
         let representativeId = memberIds[0]
         let maxOffset = -1
         memberIds.forEach((id) => {
            const entry = stackedActivities.find((s) => s.name === id)
            if (entry && entry.heightOffset > maxOffset) {
               maxOffset = entry.heightOffset
               representativeId = id
            }
         })
         memberIds.forEach((id) => meta.set(id, { size: memberIds.length, representativeId, memberIds }))
      })
      return meta
   }, [activityClusters, stackedActivities])

   const getActivityColor = (activity: Activity) => stackedActivities.find((s) => s.name === activity.id)?.color ?? '#74808F'

   // Dense clusters (3+ overlapping activities) get a tighter, capped radial stack — the labels are already
   // collapsed into a single badge, so bars beyond CLUSTER_MAX_LEVEL pile up at the same radius instead of
   // pushing the chart (and its center) further down the page for every extra overlapping activity.
   const getEffectiveOffset = (entry: { name: string; heightOffset: number }) => {
      if (!clusterMeta.has(entry.name)) return entry.heightOffset
      const level = Math.min(entry.heightOffset / 20, CLUSTER_MAX_LEVEL)
      return level * CLUSTER_LEVEL_HEIGHT
   }

   // Fixed regardless of actual activity count/stacking — otherwise the wheel's own center would shift
   // vertically every time an activity gets added or removed from a dense cluster.
   const maxRadius =
      340 + // base outer radius
      MAX_ACTIVITY_OFFSET +
      80 // buffer for labels

   const chartSize = maxRadius * 2 // diameter

   const onActivityClick = (activity: any) => {
      const selectedActivity = activities.find((x) => x.id === activity.name)
      if (selectedActivity) {
         onActivitySelect(selectedActivity)
      }
   }

   type Hover = { id: string; x: number; y: number } | null
   const [hover, setHover] = React.useState<Hover>(null)
   const [listState, setListState] = useState<{ title: string; activities: Activity[] } | null>(null)

   const onPeriodClick = (periodType: 'quarter' | 'month' | 'week', periodValue: string | number) => {
      if (periodValue === null || periodValue === undefined) return
      const { start, end, label } = getPeriodDateRange(periodType, periodValue, year.name)
      setListState({ title: label, activities: getActivitiesInPeriod(activities, start, end) })
   }

   // One row per ring. Adding a ring must not be able to express "a ring whose labels take pointer
   // events" — the label styling below always composes classes.periodLabel.
   // Hover feedback deliberately needs no field here either: classes.periodCell carries it and every
   // non-empty cell gets that class, so a new ring inherits the affordance rather than depending on
   // someone remembering to pick a hover colour for it. That only works because the effect is
   // relative (fill-opacity) instead of a colour — see HOVER_FADE_OPACITY.
   const periodRings: PeriodRing[] = useMemo(
      () => [
         {
            periodType: 'quarter',
            data: quarters(year.name).map((quarter) => ({ label: quarter.name, value: quarter.value })),
            innerRadius: 100,
            outerRadius: 180,
            fill: '#FFDF9E',
            labelClass: classes.quarters,
         },
         {
            periodType: 'month',
            data: months(year.name).map((month) => ({ label: month.name, value: month.value })),
            innerRadius: 184,
            outerRadius: 260,
            fill: tokens.colorBrandBackground3Static,
            labelClass: classes.months,
         },
         {
            periodType: 'week',
            data: weeks(year.name).map((week) => ({ label: week.number, value: week.value })),
            innerRadius: 262,
            outerRadius: 320,
            fill: tokens.colorBrandBackground3Static,
            cellStroke: tokens.colorNeutralForegroundOnBrand,
            labelClass: classes.weeks,
         },
      ],
      [year.name, classes.quarters, classes.months, classes.weeks]
   )

   const OFFSET = 14 // px offset so the tooltip doesn't sit under the cursor
   const RADIAN = Math.PI / 180
   const renderCustomizedLabel = (props: any) => {
      const { cx, cy, midAngle, outerRadius, payload } = props
      const radius = outerRadius + 20
      const x = cx + radius * Math.cos(-midAngle * RADIAN)
      const y = cy + radius * Math.sin(-midAngle * RADIAN)

      const activity = activities.find((v) => v.id === payload.name)
      if (!activity) return null

      const cluster = clusterMeta.get(activity.id)
      if (cluster) {
         if (cluster.representativeId !== activity.id) return null // non-representative members of a dense cluster render no label
         const clusterActivities = activities.filter((a) => cluster.memberIds.includes(a.id))
         return (
            <foreignObject x={x - 18} y={y - 18} width={36} height={36} style={{ overflow: 'visible' }}>
               <ClusterCountBadge count={cluster.size} onClick={() => setListState({ title: t('OverlappingActivities'), activities: clusterActivities })} />
            </foreignObject>
         )
      }

      const onMove = (e: React.MouseEvent<SVGTextElement>) => {
         setHover({ id: activity.id, x: e.clientX + OFFSET, y: e.clientY + OFFSET })
      }

      return (
         <text
            x={x}
            y={y}
            fill={tokens.colorNeutralForeground1}
            textAnchor={x > cx ? 'start' : 'end'}
            dominantBaseline="central"
            className={classes.activityLabel}
            onClick={() => {
               onActivitySelect(activity)
            }}
            onMouseMove={onMove}
            onMouseLeave={() => setHover(null)}
         >
            {payload.title || payload.name}
         </text>
      )
   }

   return (
      <div className={classes.container}>
         <PieChart width={screenWidth} height={chartSize} onMouseLeave={() => setHover(null)}>
            <Pie
               isAnimationActive={false}
               data={[year]}
               dataKey="value"
               nameKey="name"
               cx="50%"
               cy="50%"
               outerRadius={100}
               fill="#FFFFFF"
               style={{ pointerEvents: 'none' }}
               activeIndex={-1}
            >
               <Cell key={`cell-${year.value}`} fill="#FFFFFF" />
               <Label
                  content={
                     <g>
                        <foreignObject x="46%" y="49%" width="20" height="20" fill="#000000">
                           <Button
                              size="small"
                              appearance="transparent"
                              icon={<ChevronLeft24Regular />}
                              onClick={() => setYear({ name: year.name - 1, value: 1 })}
                           />
                        </foreignObject>
                        <text x="50%" y="50%" textAnchor="middle" dominantBaseline="middle" fill="#000000" className={classes.year}>
                           {year.name}
                        </text>
                        <foreignObject x="53%" y="49%" width="20" height="20" fill="#000000">
                           <Button
                              size="small"
                              appearance="transparent"
                              icon={<ChevronRight24Regular />}
                              onClick={() => setYear({ name: year.name + 1, value: 1 })}
                           />
                        </foreignObject>
                     </g>
                  }
               />
            </Pie>
            {/*
             * The period rings (quarter / month / week) are one interaction surface each: the sector
             * <path> carries `cursor: pointer`, the hover fade and `onClick`, and the LabelList text is
             * decoration that classes.periodLabel makes pointer-transparent — so hovering a glyph lights up
             * the sector it sits on. Adding a ring = adding a row to periodRings.
             *
             * These <Pie> elements MUST stay direct children of <PieChart>. PieChart discovers its
             * graphical children with findAllByType(children, Pie), which matches on `type.displayName`;
             * toArray() flattens arrays and fragments but not components, so a <PeriodRing/> wrapper
             * component would be silently dropped from the chart — no error, no warning. An inline .map()
             * is fine, and it is what the activity arcs below already do.
             */}
            {periodRings.map((ring) => (
               <Pie
                  key={`period-ring-${ring.periodType}`}
                  isAnimationActive={false}
                  data={ring.data}
                  dataKey="value"
                  nameKey="label"
                  cx="50%"
                  cy="50%"
                  innerRadius={ring.innerRadius}
                  outerRadius={ring.outerRadius}
                  startAngle={90}
                  endAngle={-270}
                  fill={ring.fill}
                  activeIndex={-1}
               >
                  {ring.data.map((entry, index) => {
                     const isEmptyCell = entry.label === null || entry.label === undefined
                     // Spread the stroke key conditionally — see the note on PeriodRing['cellStroke'].
                     const strokeProp = isEmptyCell ? { stroke: 'transparent' } : ring.cellStroke ? { stroke: ring.cellStroke } : {}
                     return (
                        <Cell
                           key={`${ring.periodType}-cell-${index}`}
                           fill={isEmptyCell ? 'transparent' : ring.fill}
                           {...strokeProp}
                           className={isEmptyCell ? undefined : classes.periodCell}
                           onClick={isEmptyCell ? undefined : () => onPeriodClick(ring.periodType, entry.label as string | number)}
                        />
                     )
                  })}
                  <LabelList dataKey="label" position="inside" className={mergeClasses(ring.labelClass, classes.periodLabel)} />
               </Pie>
            ))}
            {stackedActivities
               .sort((a, b) => b.heightOffset - a.heightOffset)
               .map((entry) => {
                  const baseInnerRadius = 320
                  const baseOuterRadius = 340

                  const effectiveOffset = getEffectiveOffset(entry)

                  return (
                     <Pie
                        isAnimationActive={false}
                        activeIndex={-1}
                        key={`activity-${entry.name}`}
                        data={[{ name: entry.name, value: 1, title: entry.title }]}
                        dataKey="value"
                        nameKey="name"
                        cx="50%"
                        cy="50%"
                        innerRadius={baseInnerRadius - 1}
                        outerRadius={baseOuterRadius + effectiveOffset}
                        startAngle={entry.startAngle}
                        endAngle={entry.endAngle}
                        fill={entry.color}
                        stroke={entry.color}
                        style={{ zIndex: entry.zIndex, position: 'relative' }}
                        label={renderCustomizedLabel}
                        labelLine={false}
                     >
                        <Cell
                           key={`activity-${entry.name}`}
                           className={classes.activityArc}
                           onClick={() => {
                              onActivityClick(entry)
                           }}
                           onMouseMove={(e) => {
                              setHover({ id: entry.name, x: e.clientX + OFFSET, y: e.clientY + OFFSET })
                           }}
                           onMouseLeave={() => setHover(null)}
                        />
                     </Pie>
                  )
               })}
         </PieChart>

         <ActivityTooltip activities={activities} hover={hover} />
         <ModalActivityList
            open={!!listState}
            title={listState?.title ?? ''}
            activities={listState?.activities ?? []}
            getColor={getActivityColor}
            onActivityClick={(activity) => {
               setListState(null)
               onActivitySelect(activity)
            }}
            close={() => setListState(null)}
         />
      </div>
   )
}
